/**
 * HazePun Pun Repository & Rotation Engine
 * Preserves the original 2015 classics and adds modern Singaporean zingers.
 */

export interface Pun {
  id?: string;
  text: string;
  classic?: boolean;
  custom?: boolean;
  author?: string;
  timestamp?: number;
}

export const PUN_LIBRARY: Pun[] = [
  { text: 'You got me haze over heels for this clean blue sky.', classic: true },
  { text: 'Clean tropical air? Truly un-haze-lievable!', classic: false },
  { text: 'Despite the ups and downs in air quality, we remain unhazed.', classic: true },
  { text: 'Cost of living is like the PSI now, up and up.', classic: true },
  { text: 'Brunch idea: eggs benny with holland-haze sauce.', classic: true },
  { text: 'The perpetrators of these forest fires should be pun-ished.', classic: true },
  { text: 'Now playing in Indonesian clubs: Major Hazer.', classic: true },
  { text: 'Jay-Z skipped Singapore. The haze gave him 99 problems.', classic: false },
  { text: 'Smoke gets in your eyes. And all over your 24-hour PSI.', classic: false },
  { text: "Hay-Z, Jay-Z's cousin, is in town dropping smoky beats.", classic: true },
  { text: 'The air is thick. Like a haze-lnut frappucino with extra foam.', classic: true },
  { text: 'Is it foggy, or did someone just fry 10,000 plates of Char Kway Teow?', classic: false },
  { text: 'Air purifier sales rep: "Allow me to introduce myself."', classic: false },
  { text: "Slightly hazy today. Don't worry, still un-hazed and unfazed.", classic: false },
  { text: 'With so many trees burned, I hoped someone kept a log of it.', classic: true },
  { text: 'The colour of the air now is 黑色 (haze se).', classic: true },
  { text: 'Set hazers to stun.', classic: true },
  { text: "N95 masks: Singapore's unofficial high-fashion accessory.", classic: false },
  { text: "My lungs are inhaling 20 packs a day and I don't even smoke.", classic: false },
  {
    text: 'Ordered GrabFood. Delivery rider arrived looking like Bane from Batman.',
    classic: false,
  },
  {
    text: 'Ghost month came late this year, or is the entire island just ghosting?',
    classic: false,
  },
  { text: "Visibility: Can't even see my CPF retirement payout.", classic: false },
  {
    text: 'Chagee tea stores running out of air: "Sorry, 0% sugar, 100% haze only."',
    classic: false,
  },
];

const STORAGE_KEY_SEEN = 'hazepun_seen_indices';
const STORAGE_KEY_CUSTOM = 'hazepun_custom_submissions';

export function getCustomPuns(): Pun[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CUSTOM);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isPun) : [];
  } catch {
    return [];
  }
}

function isPun(value: unknown): value is Pun {
  return (
    typeof value === 'object' &&
    value !== null &&
    'text' in value &&
    typeof value.text === 'string' &&
    value.text.trim().length > 0 &&
    (!('author' in value) || typeof value.author === 'string') &&
    (!('custom' in value) || typeof value.custom === 'boolean') &&
    (!('classic' in value) || typeof value.classic === 'boolean') &&
    (!('timestamp' in value) ||
      (typeof value.timestamp === 'number' && Number.isFinite(value.timestamp))) &&
    (!('id' in value) || typeof value.id === 'string')
  );
}

/** Stable content identity also works for saved puns from older releases. */
export function punId(pun: Pun): string {
  if (pun.id) return pun.id;
  const content = `${pun.custom ? 'custom' : 'original'}\0${pun.text}\0${pun.author ?? ''}\0${pun.timestamp ?? ''}`;
  let hash = 2166136261;
  for (const character of content) hash = Math.imul(hash ^ character.codePointAt(0)!, 16777619);
  return 'pun-' + (hash >>> 0).toString(36);
}

export function saveCustomPun(text: string, author: string): Pun {
  const trimmed = text.trim();
  const name = author.trim() || 'Anonymous Singaporean';
  if (!trimmed || trimmed.length > 280)
    throw new Error('Use between 1 and 280 characters for your pun.');
  if (name.length > 60) throw new Error('Use no more than 60 characters for your name.');
  const newPun: Pun = { text: trimmed, author: name, timestamp: Date.now(), custom: true };
  newPun.id = punId(newPun);
  try {
    localStorage.setItem(
      STORAGE_KEY_CUSTOM,
      JSON.stringify([newPun, ...getCustomPuns()].slice(0, 50)),
    );
  } catch {
    throw new Error('This browser could not save your pun.');
  }
  return newPun;
}

/** Migrate index histories once; new histories survive pool insertions and reordering. */
export function readSeenPuns(combined: Pun[]): Set<string> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY_SEEN) || '[]');
    if (!Array.isArray(parsed)) return new Set();
    const validIds = new Set(combined.map(punId));
    return new Set(
      parsed.flatMap((entry: unknown) => {
        if (typeof entry === 'string' && validIds.has(entry)) return [entry];
        if (typeof entry === 'number' && Number.isInteger(entry) && combined[entry])
          return [punId(combined[entry])];
        return [];
      }),
    );
  } catch {
    return new Set();
  }
}

/** Draw from one shared pool and rotation history for all air-quality statuses. */
export function getRandomPun(): Pun {
  const combined = [
    ...new Map([...PUN_LIBRARY, ...getCustomPuns()].map((pun) => [punId(pun), pun])).values(),
  ];
  const seen = readSeenPuns(combined);
  let available = combined.filter((pun) => !seen.has(punId(pun)));
  if (!available.length) {
    seen.clear();
    available = combined;
  }
  const chosen = available[Math.floor(Math.random() * available.length)] ?? PUN_LIBRARY[0];
  seen.add(punId(chosen));
  try {
    localStorage.setItem(STORAGE_KEY_SEEN, JSON.stringify([...seen]));
  } catch {
    /* Puns still work without storage. */
  }
  return chosen;
}
